<template>
  <div class="score-modal" role="alert">
    <div
      class="score-modal__background"
      @click.stop="$emit('close-score-modal')"
    >
      <canvas class="score-modal__confetti" id="confetti"></canvas>
    </div>
    <div class="score-modal__wrapper-scroller">
      <div class="score-modal__wrapper">
        <button @click="$emit('close-score-modal')" class="score-modal__close">
          <span class="score-modal__close--icon material-icons">clear</span>
        </button>
        <div class="score-modal__body">
          <div class="score-modal__scoring">
            <img
              class="score-modal__responsive__desktop--only"
              :src="statusImage"
            />
            <div
              v-if="globalHasPassed"
              class="score-modal__details score-modal__passed score-modal__details--success"
            >
              <div class="score-modal__message">Congratulations!</div>
              You passed
              <div class="score-modal__responsive__from-tablet">
                <router-link
                  v-if="globalHasPassed"
                  :to="{
                    name: 'certificate-verified',
                    params: {
                      attemptId: attempt.id,
                    },
                  }"
                  class="btn btn--primary score-modal__btn score-modal__btn--tablet"
                >
                  Go to certificate
                </router-link>
              </div>
            </div>
            <div v-else class="score-modal__details score-modal__details--fail">
              <div class="score-modal__message">We're sorry.</div>
              You didn't get the minimum score
              <div class="score-modal__message__info">
                {{ sorryMessage }}
              </div>
            </div>
            <div class="score-modal__mobile-button-wrapper">
              <button
                :class="[
                  details
                    ? 'btn btn--secondary score-modal__mobile-button--details'
                    : 'btn btn--primary',
                  'score-modal__mobile-button',
                ]"
                @click="details = false"
              >
                Overview
              </button>
              <button
                :class="[
                  details
                    ? 'btn btn--primary'
                    : 'btn btn--secondary score-modal__mobile-button--details',
                  'score-modal__mobile-button',
                ]"
                @click="details = true"
              >
                Details
              </button>
            </div>
          </div>
          <div
            :class="[
              'score-modal__grading',
              details && 'score-modal__grading--details',
            ]"
          >
            <div
              :class="[
                'score-modal__questions',
                !details &&
                  'separators__bottom-border separators__bottom-border__mobile--only',
              ]"
            >
              <h3 class="score-modal__questions__title">
                Question Results
                <span
                  class="score-modal__pill"
                  :class="
                    questionsPassed
                      ? 'score-modal__pill--success'
                      : 'score-modal__pill--fail'
                  "
                  >{{ questionsResultStatus }}</span
                >
              </h3>
              <div class="score-modal__questions__group">
                <div class="score-modal__questions__subtitle-wrapper">
                  <div class="score-modal__questions__subtitle">
                    Overall score
                  </div>
                  <div
                    class="score-modal__questions__subtitle-minimum score-modal__responsive__from-tablet"
                  >
                    Minimum score required: {{ questionsMinimumPerc }}
                  </div>
                </div>
                <div class="score-modal__questions__grading-result">
                  <PercentageBar
                    :percentage="attemptScore"
                    :passed="questionsPassed"
                  />
                  {{ questionsResultPerc }}
                  <div
                    class="score-modal__questions__subtitle-minimum score-modal__responsive__mobile--only"
                  >
                    Minimum score required: {{ questionsMinimumPerc }}
                  </div>
                </div>
              </div>
              <h5 v-if="!details" class="score-modal__overview">
                Objectives
                <span class="score-modal__overview__minimum"
                  ><CheckCircleIcon
                    class="score-modal__overview__success"
                    v-if="questionsPassed"
                  />
                  <XCircleIcon class="score-modal__overview__fail" v-else />{{
                    questionsPassedText
                  }}</span
                >
              </h5>
              <div
                :class="[
                  'score-modal__questions__list flat-scrol',
                  !details && 'score-modal__responsive__from-tablet',
                ]"
              >
                <h5 class="score-modal__questions__list__title">
                  Objectives
                  <span class="score-modal__questions__list__minimum">
                    <CheckCircleIcon
                      class="score-modal__success"
                      v-if="questionsPassed"
                    />
                    <XCircleIcon class="score-modal__fail" v-else />
                    <span class="score-modal__responsive__from-tablet">{{
                      questionsPassedText
                    }}</span>
                  </span>
                </h5>
                <div
                  class="score-modal__questions__item"
                  v-for="[key, obj] in objectives"
                  :key="key"
                >
                  <span class="score-modal__questions__item__name">
                    {{ cleanObjectiveName(key) }}
                  </span>
                  <span
                    class="score-modal__questions__item__score"
                    :class="
                      !obj.objective_passed &&
                        'score-modal__questions__item__score-failed'
                    "
                  >
                    {{ obj.passed_questions }}/{{ obj.total_questions }}
                    <CheckCircleIcon
                      class="score-modal__success"
                      v-if="obj.objective_passed"
                    />
                    <XCircleIcon class="score-modal__fail" v-else />
                  </span>
                </div>
              </div>
            </div>

            <div
              :class="[
                'score-modal__lab',
                !details &&
                  'separators__bottom-border separators__bottom-border__mobile--only',
              ]"
              v-if="hasLabAttempt"
            >
              <h3 class="score-modal__lab__title">
                Hands-on Lab Results
                <span
                  class="score-modal__pill"
                  :class="
                    labPassed
                      ? 'score-modal__pill--success'
                      : 'score-modal__pill--fail'
                  "
                  >{{ labResultStatus }}</span
                >
              </h3>
              <div class="score-modal__lab__group">
                <div class="score-modal__lab__subtitle-wrapper">
                  <div class="score-modal__lab__subtitle">Overall score</div>
                  <div class="score-modal__lab__subtitle-minimum">
                    Minimum score required: {{ labMinimumPoints }}
                  </div>
                </div>
                <div class="score-modal__lab__grading-result">
                  <PercentageBar
                    :percentage="
                      roundTo2(labObjectivesResult / labObjectivesTotal)
                    "
                    :passed="labPassed"
                  />
                  {{ labObjectivesResult }} / {{ labObjectivesTotal }}
                </div>
              </div>
              <h5 v-if="!details" class="score-modal__overview">
                Objectives
                <span class="score-modal__overview__minimum"
                  ><CheckCircleIcon
                    class="score-modal__overview__success"
                    v-if="labPassed"
                  />
                  <XCircleIcon class="score-modal__overview__fail" v-else />{{
                    labPassedText
                  }}</span
                >
              </h5>
              <div
                :class="[
                  'score-modal__lab__list flat-scrol',
                  !details && 'score-modal__responsive__from-tablet',
                ]"
              >
                <h5 class="score-modal__lab__list__title">
                  Objectives
                  <span class="score-modal__lab__list__minimum"
                    ><CheckCircleIcon
                      class="score-modal__success"
                      v-if="labPassed"
                    />
                    <XCircleIcon class="score-modal__fail" v-else />
                    <span class="score-modal__responsive__from-tablet">{{
                      labPassedText
                    }}</span>
                  </span>
                </h5>
                <div
                  class="score-modal__lab__item"
                  v-for="obj in labObjectives"
                  :key="obj.lab_objective_id"
                >
                  <span class="score-modal__lab__item__name">
                    <span class="score-modal__lab__item__pill">
                      {{ obj.points || 0 }}
                      {{ obj.points === 1 ? 'POINT' : 'POINTS' }}
                    </span>
                    <span v-html="obj.description_html"></span>
                  </span>
                  <span
                    class="score-modal__lab__item__score"
                    :class="
                      !obj.has_passed && 'score-modal__lab__item__score-failed'
                    "
                  >
                    <CheckCircleIcon
                      class="score-modal__success"
                      v-if="obj.has_passed"
                    />
                    <XCircleIcon class="score-modal__fail" v-else />
                  </span>
                </div>
              </div>
            </div>
            <div v-if="!details" class="score-modal__details-link">
              <a @click="details = true">See details</a>
            </div>
          </div>
        </div>
        <div v-if="globalHasPassed" class="score-modal__footer">
          <router-link
            :to="{
              name: 'certificate-verified',
              params: {
                attemptId: attempt.id,
              },
            }"
            class="btn btn--primary score-modal__btn"
          >
            Go to certificate
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { CheckCircleIcon, XCircleIcon } from 'vue-feather-icons'
import { Confetti } from 'vue-confetti'
import PercentageBar from '@/components/shared/BarPercentage.vue'

export default {
  name: 'ExamScoreModal',
  components: {
    CheckCircleIcon,
    XCircleIcon,
    PercentageBar,
  },
  props: {
    attempt: {
      type: Object,
      required: true,
    },
  },
  data: () => {
    return {
      confetti: null,
      options: {
        particles: [
          {
            type: 'circle',
          },
          {
            type: 'rect',
          },
        ],
        customCanvas: false,
        particlesPerFrame: 2,
        windSpeedMax: 1,
        canvasId: 'confetti',
        defaultColors: ['#A793F6', '#5B4B9B', '#ffffff', '#EE3B00', '#EF7D22'],
      },
      details: false,
    }
  },
  computed: {
    objectives() {
      return Object.entries(this.attempt.score_per_objective).sort(
        ([, a], [, b]) => {
          if (a.objective_passed === b.objective_passed) {
            return 0
          }
          if (a.objective_passed) {
            return 1
          }
          return -1
        }
      )
    },
    scores() {
      return this.attempt.score_per_objective
    },
    attemptScore() {
      return this.attempt.score ? this.roundTo2(this.attempt.score) : 0
    },
    statusImage() {
      return this.globalHasPassed
        ? require('@/assets/images/certification/success.png')
        : require('@/assets/images/certification/fail.png')
    },
    globalHasPassed() {
      return this.hasLabAttempt
        ? this.attempt.has_passed && this.attempt.user_lab_attempt.has_passed
        : this.attempt.has_passed
    },
    hasLab() {
      return !!this.attempt.practice_lab
    },
    hasLabAttempt() {
      return !!this.attempt.user_lab_attempt
    },
    sorryMessage() {
      return this.hasLab
        ? 'Passing both the Questions and Hands-on Lab sections of the exam is required to get the minimum score.'
        : 'This exam requires a minimum score on the marked objective.'
    },
    questionsResultStatus() {
      return this.attempt.has_passed ? 'Passed' : 'Failed'
    },
    questionsMinimumPerc() {
      return `${this.roundTo2(this.attempt.min_global_score_to_pass)}%`
    },
    questionsResultPerc() {
      return `${this.roundTo2(this.attempt.score)}%`
    },
    questionsPassed() {
      return Boolean(this.attempt.has_passed)
    },
    questionsPassedText() {
      return this.questionsPassed
        ? 'Minimum per objective reached'
        : 'Minimum per objective not reached'
    },
    labResultStatus() {
      return this.attempt.user_lab_attempt.has_passed ? 'Passed' : 'Failed'
    },
    labMinimumPoints() {
      return `${this.attempt.user_lab_attempt.min_points_to_pass} points`
    },
    labPassed() {
      return Boolean(this.attempt.user_lab_attempt.has_passed)
    },
    labPassedText() {
      return this.labPassed
        ? 'Minimum score reached'
        : 'Minimum score not reached'
    },
    labObjectives() {
      return this.attempt.user_lab_attempt.lab_objectives
    },
    labObjectivesResult() {
      let totalLab = 0
      this.attempt.user_lab_attempt.lab_objectives.map(o => {
        if (o.has_passed) totalLab += parseInt(o.points)
      })
      return totalLab
    },
    labObjectivesTotal() {
      let totalLab = 0
      this.attempt.user_lab_attempt.lab_objectives.map(o => {
        totalLab += parseInt(o.points)
      })
      return totalLab
    },
    showStatusImage() {
      return !this.isMobile && !this.isTablet
    },
  },
  methods: {
    roundTo2(n) {
      return Math.round((n * 100 + Number.EPSILON) * 100) / 100
    },
    closeModal() {
      this.$emit('close-score-modal')
    },
    cleanObjectiveName(name) {
      return name.replace(/^([0-9]+. )/, '')
    },
  },
  mounted() {
    this.isMobile = window.innerWidth <= 375
    this.isTablet = window.innerWidth <= 768 && !this.isMobile
    this.$nextTick(() => {
      window.addEventListener('resize', () => {
        this.windowWidth(window.innerWidth)
      })
    })
    this.details = !this.isMobile
    if (this.globalHasPassed) {
      const canvas = document.getElementById('confetti')
      canvas.width = document.body.clientWidth // document.width is obsolete
      canvas.height = document.body.clientHeight // document.height is obsolete
      this.confetti = new Confetti()
      this.confetti.start(this.options)
      setTimeout(() => {
        this.confetti.stop()
      }, 7000)
    }
  },
  created() {
    const body = document.querySelector('body')
    body.classList.add('score-modal-open')
  },
  destroyed() {
    const body = document.querySelector('body')
    body.classList.remove('score-modal-open')
    if (this.confetti) {
      this.confetti.stop()
      this.confetti = null
    }
    window.removeEventListener('resize', () => null)
  },
}
</script>
<style lang="scss" scoped>
.score-modal {
  $this: &;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 99999;
  width: 100%;
  height: 100%;
  transition: opacity 0.3s ease;
  display: flex;
  align-items: center;

  @include mobile {
    padding: 15px;
  }

  @include desktop {
    padding: 5vh;
  }
  &__wrapper-scroller {
    overflow: auto;
    width: 100%;
    height: 100%;
    @include desktop {
      display: flex;
      align-items: center;
    }
  }
  &__wrapper {
    display: flex;
    flex-wrap: wrap;
    position: relative;
    margin: 0px auto;

    color: var(--white);
    max-width: 1692px;
    font-size: rem(16px);
    width: 100%;
    justify-content: space-evenly;
    align-content: center;

    background-color: var(--darker-gray);
    padding: 15px;

    @include mobile {
      padding: 0;
    }
    @include desktop {
      border-radius: 35px;
      max-height: 1200px;
      width: 1260px;
    }
  }
  &__background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--black);
    backdrop-filter: saturate(180%) blur(20px);
  }
  &__close {
    width: 50px;
    height: 50px;
    position: absolute;
    right: 5px;
    top: 10px;
    background: none;
    border: none;
    cursor: pointer;
    color: white;

    @include tablet {
      right: 20px;
    }
    &--icon {
      fill: var(--white);
    }
  }
  &__body {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    max-width: 100vw !important;
    @include mobile {
      align-items: stretch;
    }
    @include tablet {
      width: 1260px;
      max-height: 90vh !important;
    }
  }
  &__details {
    font-size: rem(18px);
    line-height: 24px;
    text-align: center;
    @include mobile {
      font-size: rem(24px);
      line-height: 32px;
    }

    &-link {
      display: flex;
      justify-content: flex-end;

      @include mobile {
        display: none;
      }
    }

    &--success {
      background-image: url('../../assets/images/certification/success-mob.png');
      background-repeat: no-repeat;
      background-size: cover;
      width: 90%;
      padding-bottom: 10px;

      @include mobile {
        background-image: none;
      }
    }

    &--fail {
      background-image: url('../../assets/images/certification/fail-mob.png');
      background-repeat: no-repeat;
      background-size: contain;

      @include mobile {
        background-image: none;
      }
    }
  }
  &__message {
    font-family: var(--font-bold);
    font-weight: bold;
    font-size: rem(32px);
    line-height: 38px;
    color: var(--carnation);

    @include mobile {
      font-size: rem(40px);
      line-height: 52px;
    }

    &__info {
      font-size: rem(12px);
      line-height: 14px;
      color: var(white);
      position: relative;
      margin: 20px auto 0 auto;

      @include mobile {
        margin-bottom: 30px;
        font-size: rem(14px);
        line-height: 24px;
      }

      @include tablet {
        width: 93%;
        margin-bottom: 20px;
      }
    }
  }
  &__mobile-button {
    width: 50%;
    &--details {
      border: 0px;
    }
  }
  &__mobile-button-wrapper {
    width: 100%;
    margin-bottom: 30px;
    margin-top: 23px;
    background: var(--darker-gray);
    border-radius: 8px;

    @include mobile {
      display: none;
    }
  }
  &__mobile-button {
    width: 50%;
    &--details {
      background-color: var(--darker-gray);
      border: 0px;
    }
  }
  &__mobile-button-wrapper {
    width: 100%;
    margin-bottom: 30px;
    margin-top: 23px;

    @include mobile {
      display: none;
    }
  }
  &__mobile-button {
    width: 50%;
    &--details {
      background-color: var(--darker-gray);
      border: 0px;
    }
  }
  &__mobile-button-wrapper {
    width: 100%;
    margin-bottom: 30px;
    margin-top: 23px;
  }
  &__passed &__message {
    color: var(--positive-light);
  }
  &__btn {
    margin-top: 30px;

    @include mobile {
      margin-bottom: 30px;
    }
    @include desktop {
      margin-top: 50px;
      margin-bottom: 0;
    }
  }
  &__scoring {
    display: grid;
    justify-items: center;
    align-content: center;
    padding-top: 67px;
    width: 100%;

    @include tablet {
      padding: 70px 30px;
      max-width: 370px;
    }
    @include desktop {
      padding: 80px;
      max-width: 500px;
      align-content: space-between;
    }

    img {
      width: 100%;
      max-width: 280px;
      @include desktop {
        max-width: unset;
      }
    }
  }
  &__grading {
    background: var(--glass-mid-gray);
    border-radius: 10px;
    padding: 30px;
    min-width: 345px;
    width: 100%;
    @include mobile {
      flex: 1;
      padding: 50px;
      border-radius: 35px;
    }
    @include tablet {
      max-height: 90vh;
      overflow: scroll;
    }
    &--details {
      background: none;
      padding: 0;
      border-radius: 0;

      @include mobile {
        background: var(--glass-mid-gray);
        padding: 50px;
        border-radius: 35px;
      }
    }
  }
  &__questions,
  &__lab {
    margin-bottom: 20px;
    @include tablet {
      margin-bottom: rem(50px);
    }
    &__title {
      font-size: rem(20px);
      line-height: 26px;
      margin-top: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;

      @include mobile {
        font-size: rem(32px);
        line-height: 100%;
        justify-content: flex-start;
      }

      @include tablet {
        font-family: var(--font-bold);
        font-weight: bold;
      }
    }
    &__subtitle {
      font-size: rem(16px);
      line-height: 23px;

      @include mobile {
        font-size: rem(20px);
        line-height: 26px;
      }

      &-minimum {
        color: var(--light-blue-gray);
        margin-bottom: 10px;
        font-size: rem(10px);
        line-height: 23px;

        @include mobile {
          font-size: rem(16px);
          line-height: 24px;
        }
      }
    }
    &__group {
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      @include mobile {
        flex-direction: row;
        align-items: center;
      }
    }
    &__grading-result {
      display: flex;
      flex-wrap: wrap;
      align-items: center;

      ::v-deep .bar-percentage {
        margin-right: 10px;
      }
    }
    &__list {
      background: linear-gradient(
        111.02deg,
        rgba(18, 18, 18, 0.7) 3.86%,
        rgba(18, 18, 18, 0) 107.96%
      );
      border-radius: 10px;
      padding: 20px;
      &__title {
        font-size: rem(16px);
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 0;
        margin-bottom: 10px;
      }
      &__minimum {
        display: flex;
        align-items: center;

        @include mobile {
          svg {
            margin-right: 10px;
          }
        }
      }
    }
    &__item {
      font-size: rem(18px);
      display: flex;
      padding: 10px 0;
      border-bottom: 1px solid var(--tundora-gray);

      @include tablet {
        padding: 5px 0;
      }
      &:last-child {
        border-bottom: none;
      }
      &__name {
        line-height: 24px;
        color: var(--light-blue-gray);
        max-width: 85%;
        font-size: rem(12px);
        line-height: 14px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        @include mobile {
          font-size: rem(14px);
          line-height: normal;
          flex-direction: row;
          align-items: center;
        }
      }
      &__score {
        margin-left: auto;
        display: flex;
        align-items: center;
        svg {
          margin-left: 10px;
        }
      }
      &__score-failed {
        position: relative;
        min-width: 34px;
      }
      &__pill {
        padding: 9px 14px;
        font-size: rem(12px);
        line-height: 14px;
        background: linear-gradient(
          112.2deg,
          rgba(187, 188, 199, 0.4) -46.39%,
          rgba(187, 188, 199, 0) 112.38%
        );
        border-radius: 20px;
        border: 1px solid #3e3f43;
        color: var(--white);
        margin-right: 10px;
        margin-bottom: 5px;
        white-space: nowrap;
        @include desktop {
          margin-right: 30px;
        }
      }
      ::v-deep p {
        margin: 0;
      }
    }
    &__objectives {
      font-size: rem(16px);
      line-height: 23px;

      display: flex;
      flex-direction: column;

      &__minimum {
        font-size: rem(14px);
        line-height: 20px;

        display: flex;
        flex-direction: row-reverse;
        justify-content: space-between;
        align-items: center;
      }
      &__success {
        stroke: var(--caribbean-green);
      }
      &__fail {
        stroke: var(--carnation);
      }
    }
  }
  &__questions {
    &__item {
      &__name {
        max-width: 233px;

        @include mobile {
          max-width: unset;
        }
      }
    }
  }
  &__success {
    stroke: var(--caribbean-green);
  }
  &__fail {
    stroke: var(--carnation);
  }
  &__pill {
    padding: 0 8px;
    border-radius: 30px;
    color: #1e1e1e;
    font-size: 0.75rem;
    display: inline-block;
    height: 24px;
    display: flex;
    align-items: center;
    font-weight: normal;
    font-family: var(--font-medium);
    margin-left: 10px;

    min-width: 77px;
    justify-content: center;
    @include tablet {
      min-width: unset;
      padding: 5px 8px;
    }
    &--success {
      background: #70ffd4;
      box-shadow: 0px 0px 10px rgba(112, 255, 212, 0.5);
    }
    &--fail {
      background: var(--carnation);
      border: 1px solid var(--carnation);
      box-sizing: border-box;
      box-shadow: 0px 0px 10px rgba(255, 93, 93, 0.5);
      border-radius: 30px;
    }
  }
  &__confetti {
    width: 100%;
  }

  &__overview {
    font-size: rem(16px);
    line-height: 23px;

    display: flex;
    flex-direction: column;
    @include mobile {
      display: none;
    }

    &__minimum {
      font-size: rem(14px);
      line-height: 20px;

      display: flex;
      flex-direction: row-reverse;
      justify-content: space-between;
      align-items: center;
    }
    &__success {
      stroke: var(--caribbean-green);
    }
    &__fail {
      stroke: var(--carnation);
    }
  }
  &__footer {
    display: flex;
    width: 100%;
    justify-content: center;

    a {
      width: 100%;
    }

    @include mobile {
      display: none;
    }
  }
  &__responsive {
    &__from-tablet {
      display: none;

      @include mobile {
        display: inherit;
      }
    }
    &__desktop--only {
      display: none;

      @include tablet {
        display: inherit;
      }
    }
    &__mobile--only {
      @include mobile {
        display: none;
      }
    }
  }
}
</style>
